/**
 * keepUnchanged - a listener's delivery, with what did not change kept as it was (plan sync-listeners, 2026-10-31).
 *
 * Every snapshot parses new objects for every document in a collection. Set as state as they come, each delivery is
 * a new list of new items: the shell renders from the root and every memo keyed on an item recomputes, though most
 * deliveries change one item or none. Here each item that reads the same as the one held (by value, key order
 * ignored - Firestore moves a field it just wrote to the end) is the held object, and a delivery that changes
 * nothing is the held list itself, so React's state setter bails out and nothing renders.
 *
 * Order counts: a list in a new order is a new list (a map's order does not). Pure.
 */
import { sameValue } from '../engine/canonicalJson';

/** True when both lists hold the very same objects, in the same order. */
function sameItems<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((item, i) => item === b[i]);
}

export function keepUnchanged<T>(held: readonly T[], next: readonly T[], idOf: (item: T) => string): T[] {
  const byId = new Map(held.map((item) => [idOf(item), item]));
  const kept = next.map((item) => {
    const before = byId.get(idOf(item));
    return before !== undefined && sameValue(before, item) ? before : item;
  });
  return sameItems(kept, held) ? (held as T[]) : kept;
}

/** The same for a map: each value that reads the same is the held one; a map that changes nothing is the held map. */
export function keepUnchangedMap<K, V>(held: ReadonlyMap<K, V>, next: ReadonlyMap<K, V>): Map<K, V> {
  const kept = new Map<K, V>();
  for (const [key, value] of next) {
    const before = held.get(key);
    kept.set(key, before !== undefined && sameValue(before, value) ? before : value);
  }
  const same = kept.size === held.size && [...kept].every(([key, value]) => held.get(key) === value);
  return same ? (held as Map<K, V>) : kept;
}
